import { DUE_SOON_DAYS, DUE_SOON_KM, dueServices, lineMatchesPlanItem, todayIso, type DueService, type PlanItem, type ServiceInterval, type WorkOrderLineInput } from "@szerviz/core";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp, useFeature } from "../context";
import type { ServiceHistoryOrder, Vehicle, VehicleFact, WorkOrderItem } from "../data";
import { ErrorText } from "./ErrorText";
import { Help } from "./Help";

/** A line of an earlier work order offered again (the customer is asked again when quotes are on). */
export function repeatLine(l: WorkOrderItem, quotesOn: boolean): WorkOrderLineInput {
  return {
    kind: l.kind, description: l.description, partNumber: l.partNumber ?? null, articleId: l.articleId ?? null, unit: l.unit ?? null,
    quantity: l.quantity, unitPrice: l.unitPrice, priceMode: l.priceMode, vatRate: l.vatRate, customerSupplied: l.customerSupplied ?? false,
    optional: false, approval: quotesOn ? "pending" : "none",
  };
}

/**
 * Due services when the odometer is entered (feature "dueServices"): the type data sheet's
 * maintenance plan against the vehicle's own closed work orders. Only a warning; on a work order
 * the lines done last time can be added again (on the new work order they are added on creating it).
 */
export function DueServices({ vehicle, km, items, onAdd, picked, onPick }: {
  vehicle: Vehicle | null;
  km: number | null;
  /** Lines already on the open work order: an item done there is not offered again. */
  items?: readonly WorkOrderItem[];
  /** On an open work order: adds the lines. */
  onAdd?: (lines: WorkOrderLineInput[]) => Promise<void>;
  /** On the new work order: what was chosen to be added, and the toggle. */
  picked?: readonly string[];
  onPick?: (key: string, lines: WorkOrderLineInput[] | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const { store } = useApp();
  const on = useFeature("dueServices");
  const quotesOn = useFeature("quotes");
  const [data, setData] = useState<{ facts: VehicleFact[]; history: ServiceHistoryOrder[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const vehicleId = vehicle?.id;
  const typeId = vehicle?.typeId;

  useEffect(() => {
    setData(null);
    // only for the workshop's own, classified vehicle (the data sheet rule)
    if (!on || !vehicleId || !typeId) return;
    let alive = true;
    void Promise.all([store.vehicleDataSheet(vehicleId), store.serviceHistory(vehicleId)])
      .then(([facts, history]) => alive && setData({ facts, history }))
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, [on, store, vehicleId, typeId]);

  const result = useMemo(() => (data ? dueServices({ facts: data.facts, orders: data.history, currentKm: km, today: todayIso() }) : null), [data, km]);
  if (!result || !result.hasPlan) return null;

  const n = (x: number) => x.toLocaleString(i18n.language);
  const name = (item: PlanItem) => (item.key === "oilChange" ? t("due.item.oilChange") : item.label ?? t(`fact.${item.field}`));
  const every = (iv: ServiceInterval) =>
    [iv.km !== null ? t("due.km", { km: n(iv.km) }) : null, iv.months !== null ? (iv.months % 12 === 0 ? t("due.years", { n: iv.months / 12 }) : t("due.months", { n: iv.months })) : null]
      .filter(Boolean)
      .join(" / ");
  const date = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(i18n.language);
  const lastText = (d: DueService) => (d.last.km !== null ? t("due.lastKm", { km: n(d.last.km), date: date(d.last.date) }) : t("due.lastDate", { date: date(d.last.date) }));
  // only the part that makes it due (km, time or both), not the far one
  const howFar = (d: DueService) =>
    [
      d.kmOver !== null && d.kmOver > -DUE_SOON_KM ? (d.kmOver > 0 ? t("due.kmOver", { km: n(d.kmOver) }) : t("due.kmLeft", { km: n(-d.kmOver) })) : null,
      d.daysOver !== null && d.daysOver > -DUE_SOON_DAYS ? (d.daysOver > 0 ? t("due.daysOver", { days: d.daysOver }) : t("due.daysLeft", { days: -d.daysOver })) : null,
    ].filter(Boolean).join(" · ");

  if (result.due.length === 0) {
    return result.noHistory.length > 0 ? (
      <p className="muted small due-hint wide no-print">{t("due.noHistory", { items: result.noHistory.map(name).join(", ") })}</p>
    ) : null;
  }

  const add = async (d: DueService) => {
    setError(null);
    setBusy(true);
    try {
      await onAdd!(d.lines.map((l) => repeatLine(l as WorkOrderItem, quotesOn)));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="due-services wide no-print" aria-label={t("due.title")} role="status">
      <h3>
        {t("due.title")} <Help id="due" />
      </h3>
      <ErrorText error={error} />
      <ul>
        {result.due.map((d) => {
          const onOrder = items?.some((l) => l.approval !== "declined" && lineMatchesPlanItem(d.item, l));
          const isPicked = picked?.includes(d.item.key);
          return (
            <li key={d.item.key} className={d.state === "overdue" ? "overdue" : undefined}>
              <p className="due-text">
                <strong>{t(d.state === "overdue" ? "due.overdue" : "due.due", { item: name(d.item) })}</strong> ({every(d.item.interval)}, {lastText(d)})
                {howFar(d) && <span className="due-far"> {howFar(d)}</span>}
              </p>
              {onOrder ? (
                <span className="badge ok">{t("due.onOrder")}</span>
              ) : onAdd ? (
                <button type="button" className="warn-button" disabled={busy} onClick={() => void add(d)}>
                  {t("due.add")}
                </button>
              ) : onPick ? (
                <button
                  type="button"
                  className={isPicked ? "primary" : "warn-button"}
                  aria-pressed={!!isPicked}
                  onClick={() => onPick(d.item.key, isPicked ? null : d.lines.map((l) => repeatLine(l as WorkOrderItem, quotesOn)))}
                >
                  {isPicked ? t("due.picked") : t("due.add")}
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
      {result.noHistory.length > 0 && <p className="muted small">{t("due.noHistory", { items: result.noHistory.map(name).join(", ") })}</p>}
    </section>
  );
}
